import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID
} from '@angular/core';
import { Router } from '@angular/router';
import { finalize, forkJoin, Subscription } from 'rxjs';

import { AhmedElshahatJobService } from '../../../../services/ahmed-elshahat-job';
import { CountryCityService } from '../../../../services/country-city.service';
import { JobApplicationService } from '../../../../services/job-application';

@Component({
  selector: 'app-jobsinhome',
  templateUrl: './jobsinhome.component.html',
  styleUrl: './jobsinhome.component.scss'
})
export class JobsinhomeComponent implements OnInit, OnDestroy {
  private isBrowser = false;
  private subscriptions = new Subscription();

  contentReady = false;
  currentLanguage = 'en';

  readonly maxHomeJobs = 10;

  totalJobsCount = 0;
  jobs: any[] = [];

  countries: any[] = [];
  cities: any[] = [];

  isLoading = false;
  isDetailsLoading = false;
  errorMessage = '';

  selectedJob: any = null;
  modalOpen = false;

  selectedApplyJob: any = null;
  applyModalOpen = false;
  directApplyingJobId: number | string | null = null;
  appliedJobIds = new Set<string>();

  applySuccessModalOpen = false;

  constructor(
    private router: Router,
    private jobService: AhmedElshahatJobService,
    private countryCityService: CountryCityService,
    private applicationService: JobApplicationService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.currentLanguage = (
        localStorage.getItem('language') ||
        localStorage.getItem('lang') ||
        localStorage.getItem('translocoLang') ||
        document.documentElement.lang ||
        'en'
      ).toLowerCase();
    }

    this.contentReady = true;
    this.getCountriesAndCities();
    this.getJobs();
    this.loadAppliedJobIds();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.modalOpen = false;
    this.applyModalOpen = false;
    this.applySuccessModalOpen = false;
    this.unlockBody();
  }

  get isAr(): boolean {
    return this.currentLanguage.startsWith('ar');
  }

  get visibleJobs(): any[] {
    return this.jobs.slice(0, this.maxHomeJobs);
  }

  get showMoreButton(): boolean {
    return this.contentReady && this.totalJobsCount > this.maxHomeJobs;
  }

  goToJobsPage(): void {
    this.router.navigate(['/jobs']);
  }

  getCountriesAndCities(): void {
    const sub = forkJoin({
      countries: this.countryCityService.getCountries({ per_page: 1000 }),
      cities: this.countryCityService.getCities({ per_page: 1000 })
    }).subscribe({
      next: (res: any) => {
        this.countries = this.extractArray(res.countries);
        this.cities = this.extractArray(res.cities);
      },
      error: (err: any) => {
        console.error('Countries/Cities loading error:', err);
        this.countries = [];
        this.cities = [];
      }
    });

    this.subscriptions.add(sub);
  }

  getJobs(): void {
    this.isLoading = true;
    this.errorMessage = '';

    const sub = this.jobService
      .getJobs({
        status: 'published',
        per_page: this.maxHomeJobs
      })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res: any) => {
          const paginationData = res?.data;

          this.jobs = Array.isArray(paginationData?.data)
            ? paginationData.data
            : this.extractArray(res);

          this.totalJobsCount = Number(
            paginationData?.total || this.jobs.length || 0
          );
        },
        error: err => {
          console.error('Jobs loading error:', err);

          this.jobs = [];
          this.totalJobsCount = 0;
          this.errorMessage = this.isAr
            ? 'حدث خطأ أثناء تحميل الوظائف. حاول مرة أخرى.'
            : 'Something went wrong while loading jobs. Please try again.';
        }
      });

    this.subscriptions.add(sub);
  }

  openJobDetails(job: any): void {
    if (!job?.id) return;

    this.selectedJob = job;
    this.modalOpen = true;
    this.applyModalOpen = false;
    this.applySuccessModalOpen = false;
    this.isDetailsLoading = true;
    this.lockBody();

    const viewsSub = this.jobService.increaseViews(job.id).subscribe({
      next: () => {},
      error: () => {}
    });

    const detailsSub = this.jobService
      .getJob(job.id)
      .pipe(finalize(() => (this.isDetailsLoading = false)))
      .subscribe({
        next: (res: any) => {
          this.selectedJob = res?.data || job;
        },
        error: err => {
          console.error('Job details error:', err);
          this.selectedJob = job;
        }
      });

    this.subscriptions.add(viewsSub);
    this.subscriptions.add(detailsSub);
  }

  closeJobDetails(): void {
    this.modalOpen = false;
    this.selectedJob = null;
    this.isDetailsLoading = false;

    if (!this.applyModalOpen && !this.applySuccessModalOpen) {
      this.unlockBody();
    }
  }

  applyForJob(job: any): void {
    if (this.isCompanyUser()) {
      return;
    }

    if (!job) return;

    if (this.isJobApplied(job)) {
      return;
    }

    const applicationFormType = this.getApplicationFormType(job);

    if (applicationFormType === 'doctor') {
      this.router.navigate(['/apply/doctor', job.id]);
      return;
    }

    if (applicationFormType === 'dentist') {
      this.router.navigate(['/apply/dentist', job.id]);
      return;
    }

    if (applicationFormType === 'general') {
      this.router.navigate(['/apply/any']);
      return;
    }

    this.selectedApplyJob = job;
    this.applyModalOpen = true;
    this.modalOpen = false;
    this.applySuccessModalOpen = false;
    this.isDetailsLoading = false;

    this.lockBody();
  }

  closeApplyModal(): void {
    this.applyModalOpen = false;
    this.selectedApplyJob = null;

    if (!this.modalOpen && !this.applySuccessModalOpen) {
      this.unlockBody();
    }
  }

  handleApplicationSubmitted(): void {
    if (this.selectedApplyJob?.id) {
      this.appliedJobIds.add(String(this.selectedApplyJob.id));
    }

    this.applyModalOpen = false;
    this.selectedApplyJob = null;
    this.modalOpen = false;
    this.selectedJob = null;
    this.isDetailsLoading = false;
    this.applySuccessModalOpen = true;

    this.lockBody();
  }

  closeApplySuccessModal(): void {
    this.applySuccessModalOpen = false;
    this.unlockBody();
  }

  isJobApplied(job: any): boolean {
    return Boolean(job?.id && this.appliedJobIds.has(String(job.id)));
  }

  isCompanyUser(): boolean {
    if (!this.isBrowser) {
      return false;
    }

    const directType =
      localStorage.getItem('user_type') ||
      localStorage.getItem('account_type') ||
      localStorage.getItem('role') ||
      localStorage.getItem('user_role') ||
      localStorage.getItem('type');

    if (this.normalizeUserType(directType) === 'company') {
      return true;
    }

    try {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;

      return this.normalizeUserType(
        user?.user_type ||
        user?.account_type ||
        user?.role ||
        user?.user_role ||
        user?.type ||
        user?.user?.user_type
      ) === 'company';
    } catch {
      return false;
    }
  }

  getApplyButtonLabel(job: any): string {
    if (this.isJobApplied(job)) {
      return this.isAr ? 'تم التقديم' : 'Applied';
    }

    return this.isAr ? 'قدم' : 'Apply';
  }

  getApplyNowButtonLabel(job: any): string {
    if (this.isJobApplied(job)) {
      return this.isAr ? 'تم التقديم' : 'Applied';
    }

    return this.isAr ? 'قدم الآن' : 'Apply Now';
  }

  private tryDirectApply(job: any): boolean {
    const user = this.getStoredUserWithCv();

    if (!user?.id || !job?.id) {
      return false;
    }

    if (this.directApplyingJobId) {
      return true;
    }

    this.directApplyingJobId = job.id;
    this.errorMessage = '';

    const sub = this.applicationService
      .applyToJob({
        job_id: job.id,
        user_id: user.id
      })
      .pipe(finalize(() => (this.directApplyingJobId = null)))
      .subscribe({
        next: () => {
          this.appliedJobIds.add(String(job.id));
          this.handleApplicationSubmitted();
        },
        error: (err: any) => {
          if (this.isAlreadyAppliedError(err)) {
            this.appliedJobIds.add(String(job.id));
          }

          this.errorMessage = this.getDirectApplyErrorText(err);
        }
      });

    this.subscriptions.add(sub);
    return true;
  }

  private getStoredUserWithCv(): any | null {
    if (this.isCompanyUser()) {
      return null;
    }

    if (!this.isBrowser) {
      return null;
    }

    try {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : {};
      const userId = user?.id || localStorage.getItem('user_id');
      const cv =
        user?.user_cv ||
        user?.user_cv_url ||
        user?.cv ||
        localStorage.getItem('user_cv') ||
        localStorage.getItem('user_cv_url');

      return userId && cv ? { ...user, id: userId, user_cv: cv } : null;
    } catch {
      return null;
    }
  }

  private loadAppliedJobIds(): void {
    const userId = this.getStoredUserId();

    if (!userId) {
      return;
    }

    const sub = this.applicationService.getApplicationsByUserId(userId).subscribe({
      next: (res: any) => {
        this.appliedJobIds = new Set(
          this.extractArray(res?.data || res)
            .map((application: any) => this.getApplicationJobId(application))
            .filter((jobId: any) => jobId !== null && jobId !== undefined)
            .map((jobId: any) => String(jobId))
        );
      },
      error: () => {
        this.appliedJobIds.clear();
      }
    });

    this.subscriptions.add(sub);
  }

  private getStoredUserId(): number | string | null {
    if (!this.isBrowser) {
      return null;
    }

    try {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : {};

      return user?.id || localStorage.getItem('user_id') || null;
    } catch {
      return localStorage.getItem('user_id') || null;
    }
  }

  private getApplicationJobId(application: any): number | string | null {
    return (
      application?.job_id ||
      application?.job?.id ||
      application?.ahmed_elshahat_job?.id ||
      application?.job_data?.id ||
      null
    );
  }

  private getDirectApplyErrorText(err: any): string {
    const message =
      err?.error?.message ||
      err?.error?.error ||
      (err?.status === 409 || err?.status === 422
        ? this.isAr
          ? 'لقد قدمت على هذه الوظيفة من قبل'
          : 'You have already applied for this job'
        : '');

    return (
      message ||
      (this.isAr
        ? 'تعذر التقديم الآن. حاول مرة أخرى.'
        : 'Could not apply right now. Please try again.')
    );
  }

  private isAlreadyAppliedError(err: any): boolean {
    return err?.status === 409 || err?.status === 422;
  }

  private normalizeUserType(value: any): string {
    const type = String(value || '').toLowerCase().trim();

    if (
      type === 'company' ||
      type === 'companies' ||
      type === 'employer' ||
      type === 'employers' ||
      type === 'business'
    ) {
      return 'company';
    }

    return type;
  }

  getJobTitle(job: any): string {
    return this.pickLang(
      job,
      'title_ar',
      'title_en',
      'title',
      this.isAr ? 'وظيفة بدون عنوان' : 'Untitled Job'
    );
  }

  getCompanyName(job: any): string {
    // Employer identities are intentionally private on public job cards.
    return this.isAr ? 'صاحب عمل سري' : 'Confidential Employer';
  }

  getCountryName(job: any): string {
    if (!job) return this.emptyText();

    if (job?.country && typeof job.country === 'object') {
      return this.pickLang(job.country, 'country_name_ar', 'country_name_en');
    }

    if (job?.country_name_ar || job?.country_name_en) {
      return this.pickLang(job, 'country_name_ar', 'country_name_en');
    }

    const countryValue = job?.country_id || job?.country;
    if (!countryValue) return this.emptyText();

    const country = this.countries.find((item: any) => item.id == countryValue);

    if (country) {
      return this.pickLang(country, 'country_name_ar', 'country_name_en');
    }

    if (typeof countryValue === 'string' && isNaN(Number(countryValue))) {
      return countryValue;
    }

    return this.emptyText();
  }

  getCityName(job: any): string {
    if (!job) return this.emptyText();

    if (job?.city && typeof job.city === 'object') {
      return this.pickLang(job.city, 'city_name_ar', 'city_name_en');
    }

    if (job?.city_name_ar || job?.city_name_en) {
      return this.pickLang(job, 'city_name_ar', 'city_name_en');
    }

    const cityValue = job?.city_id || job?.city;
    if (!cityValue) return this.emptyText();

    const city = this.cities.find((item: any) => item.id == cityValue);

    if (city) {
      return this.pickLang(city, 'city_name_ar', 'city_name_en');
    }

    if (typeof cityValue === 'string' && isNaN(Number(cityValue))) {
      return cityValue;
    }

    return this.emptyText();
  }

  getJobType(job: any): string {
    return this.getEmploymentTypeLabel(job?.employment_type || job?.type);
  }

  getApplicationFormType(job: any): 'general' | 'doctor' | 'dentist' {
    const jobType = String(job?.job_type || '').trim().toLowerCase();

    if (jobType === 'fordoctors') return 'doctor';
    if (jobType === 'fordentist') return 'dentist';

    return 'general';
  }

  getWorkModel(job: any): string {
    return this.getWorkModelLabel(job?.work_model);
  }

  getWorkModelLabel(value: string): string {
    const normalized = value || '';

    const ar: any = {
      on_site: 'من مقر العمل',
      onsite: 'من مقر العمل',
      remote: 'عن بُعد',
      hybrid: 'هايبرد'
    };

    const en: any = {
      on_site: 'On site',
      onsite: 'On site',
      remote: 'Remote',
      hybrid: 'Hybrid'
    };

    return this.isAr
      ? ar[normalized] || this.emptyText()
      : en[normalized] || this.emptyText();
  }

  getEmploymentTypeLabel(value: string): string {
    const normalized = value || '';

    const ar: any = {
      full_time: 'دوام كامل',
      'full-time': 'دوام كامل',
      part_time: 'دوام جزئي',
      'part-time': 'دوام جزئي',
      contract: 'تعاقد',
      internship: 'تدريب',
      freelance: 'فريلانسر',
      temporary: 'مؤقت'
    };

    const en: any = {
      full_time: 'Full time',
      'full-time': 'Full time',
      part_time: 'Part time',
      'part-time': 'Part time',
      contract: 'Contract',
      internship: 'Internship',
      freelance: 'Freelance',
      temporary: 'Temporary'
    };

    return this.isAr
      ? ar[normalized] || this.formatText(normalized)
      : en[normalized] || this.formatText(normalized);
  }

  getSalary(job: any): string {
    if (!job) return this.emptyText();

    const salaryNote = this.pickLang(
      job,
      'salary_note_ar',
      'salary_note_en',
      'salary_note',
      ''
    );

    if (salaryNote) return salaryNote;

    if (job?.salary_type === 'competitive') {
      return this.isAr ? 'راتب تنافسي' : 'Competitive salary';
    }

    if (job?.salary_type === 'negotiable') {
      return this.isAr ? 'قابل للتفاوض' : 'Negotiable';
    }

    const currency = job?.salary_currency || '';
    const hasMin = this.hasValue(job?.salary_min);
    const hasMax = this.hasValue(job?.salary_max);

    if (hasMin && hasMax) {
      return `${this.formatNumber(job.salary_min)} - ${this.formatNumber(
        job.salary_max
      )} ${currency}`.trim();
    }

    if (hasMin) {
      return this.isAr
        ? `يبدأ من ${this.formatNumber(job.salary_min)} ${currency}`.trim()
        : `From ${this.formatNumber(job.salary_min)} ${currency}`.trim();
    }

    if (hasMax) {
      return this.isAr
        ? `حتى ${this.formatNumber(job.salary_max)} ${currency}`.trim()
        : `Up to ${this.formatNumber(job.salary_max)} ${currency}`.trim();
    }

    return this.isAr ? 'الراتب غير مذكور' : 'Salary not mentioned';
  }

  getPostedAt(job: any): string {
    const dateValue = job?.posted_at || job?.created_at;

    if (!dateValue) return this.isAr ? 'حديثًا' : 'Recently';

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return this.isAr ? 'حديثًا' : 'Recently';
    }

    const now = new Date();
    const diffDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays <= 0) return this.isAr ? 'حديثًا' : 'Recently';
    if (diffDays === 1) return this.isAr ? 'أمس' : 'Yesterday';

    if (diffDays < 30) {
      return this.isAr
        ? `منذ ${this.formatNumber(diffDays)} أيام`
        : `${this.formatNumber(diffDays)} days ago`;
    }

    return date.toLocaleDateString(this.isAr ? 'ar-EG' : 'en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  getTags(job: any, limit = 0): string[] {
    if (!Array.isArray(job?.skills) || !job.skills.length) return [];

    const tags = job.skills
      .map((skill: any) => {
        if (typeof skill === 'string') return skill;
        return this.pickLang(skill, 'name_ar', 'name_en', 'name', '');
      })
      .filter(Boolean);

    return limit ? tags.slice(0, limit) : tags;
  }

  getRequirements(job: any): any[] {
    return Array.isArray(job?.requirements) ? job.requirements : [];
  }

  getJobDescription(job: any): string {
    return this.pickLang(
      job,
      'description_ar',
      'description_en',
      'description',
      ''
    );
  }

  getJobResponsibilities(job: any): string {
    return this.pickLang(
      job,
      'responsibilities_ar',
      'responsibilities_en',
      'responsibilities',
      ''
    );
  }

  getRequirementText(req: any): string {
    return this.pickLang(
      req,
      'requirement_text_ar',
      'requirement_text_en',
      'requirement_text',
      ''
    );
  }

  getRequirementTypeLabel(value: any): string {
    if (!value) return this.emptyText();

    const normalized = String(value);

    const ar: any = {
      education: 'التعليم',
      experience: 'الخبرة',
      skill: 'مهارة',
      language: 'لغة',
      certificate: 'شهادة',
      other: 'أخرى'
    };

    const en: any = {
      education: 'Education',
      experience: 'Experience',
      skill: 'Skill',
      language: 'Language',
      certificate: 'Certificate',
      other: 'Other'
    };

    return this.isAr
      ? ar[normalized] || this.formatText(normalized)
      : en[normalized] || this.formatText(normalized);
  }

  getStatusLabel(value: string): string {
    const normalized = value || 'published';

    const ar: any = {
      draft: 'مسودة',
      published: 'منشورة',
      closed: 'مغلقة'
    };

    const en: any = {
      draft: 'Draft',
      published: 'Published',
      closed: 'Closed'
    };

    return this.isAr
      ? ar[normalized] || this.formatText(normalized)
      : en[normalized] || this.formatText(normalized);
  }

  formatNumber(value: any): string {
    if (value === null || value === undefined || value === '') return '';

    const numberValue = Number(value);

    if (Number.isFinite(numberValue)) {
      return new Intl.NumberFormat(this.isAr ? 'ar-EG' : 'en-US', {
        maximumFractionDigits: 0
      }).format(numberValue);
    }

    const stringValue = String(value);

    if (!this.isAr) return stringValue;

    return stringValue.replace(/\d/g, (digit: string) => {
      return '٠١٢٣٤٥٦٧٨٩'[Number(digit)];
    });
  }

  formatText(value: any): string {
    if (!value) return this.emptyText();

    return String(value)
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  trackByJobId(index: number, job: any): number {
    return job?.id || index;
  }

  trackByRequirementId(index: number, req: any): number {
    return req?.id || index;
  }

  private extractArray(res: any): any[] {
    if (Array.isArray(res)) return res;

    if (Array.isArray(res?.data?.data)) return res.data.data;

    if (Array.isArray(res?.data)) return res.data;

    return [];
  }

  private pickLang(
    item: any,
    arKey: string,
    enKey: string,
    fallbackKey?: string,
    fallback: string = this.emptyText()
  ): string {
    if (!item) return fallback;

    if (this.isAr) {
      return (
        item?.[arKey] ||
        item?.[enKey] ||
        (fallbackKey ? item?.[fallbackKey] : '') ||
        fallback
      );
    }

    return (
      item?.[enKey] ||
      item?.[arKey] ||
      (fallbackKey ? item?.[fallbackKey] : '') ||
      fallback
    );
  }

  private emptyText(): string {
    return this.isAr ? 'غير محدد' : 'Not specified';
  }

  private hasValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  private lockBody(): void {
    if (!this.isBrowser) return;
    document.body.style.overflow = 'hidden';
  }

  private unlockBody(): void {
    if (!this.isBrowser) return;
    document.body.style.overflow = '';
  }
}
